import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import {ArrowLeft, Plus, Edit2, Trash2, X, Database, Search, Upload} from 'lucide-react'
import { useMicrocuencasVariablesCRUD } from '../hooks/useMicrocuencasVariablesCRUD'

const MicrocuencasVariables = () => {
  const navigate = useNavigate()
  const { microcuencas, loading, uploading, createMicrocuenca, updateMicrocuenca, deleteMicrocuenca, uploadCSVFile, uploadCSVFromURL, clearAllData } = useMicrocuencasVariablesCRUD()
  
  const [searchTerm, setSearchTerm] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [editingItem, setEditingItem] = useState<any>(null)
  const [formData, setFormData] = useState<any>({})
  const fileInputRef = React.useRef<HTMLInputElement>(null)

  const filteredData = microcuencas.filter(item =>
    item.microcuenca?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.municipio?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.codigo?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const handleOpenModal = (item?: any) => {
    if (item) {
      setEditingItem(item)
      setFormData(item)
    } else {
      setEditingItem(null)
      setFormData({
        codigo: '',
        municipio: '',
        microcuenca: '',
        superficie_agricola: 0,
        sup_micro: 0,
        superficie_pasto: 0,
        superficie_forestal: 0,
        densidad_poblacion: 0,
        precipitacion: 0,
        temperatura: 0,
        altitud: 0,
        pendiente: 0,
        deforestacion: 0,
        erosion: 0,
        uso_suelo: '',
        tipo_vegetacion: '',
        disponibilidad_agua: 0,
        calidad_agua: '',
        riesgo_inundacion: '',
        vulnerabilidad_sequia: ''
      })
    }
    setShowModal(true)
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setEditingItem(null)
    setFormData({})
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      if (editingItem) {
        await updateMicrocuenca(editingItem._id, formData)
      } else {
        await createMicrocuenca(formData)
      }
      handleCloseModal()
    } catch (error) {
      alert('Error al guardar el registro')
    }
  }

  const handleDelete = async (id: string) => {
    if (confirm('¿Estás seguro de eliminar este registro?')) {
      try {
        await deleteMicrocuenca(id)
      } catch (error) {
        alert('Error al eliminar el registro')
      }
    }
  }

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      if (confirm(`¿Importar datos desde "${file.name}"?`)) {
        await uploadCSVFile(file)
      }
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleImportCSV = async () => {
    if (confirm('¿Importar datos desde el archivo CSV remoto?')) {
      await uploadCSVFromURL('https://static.lumi.new/97/97e06976ba247bbea1c84ca67aedf450.csv')
    }
  }

  const handleClearAll = async () => {
    if (confirm('¿Eliminar TODOS los registros? Esta acción no se puede deshacer.')) {
      await clearAllData()
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <motion.header 
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="bg-white shadow-lg border-b"
      >
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/')}
                className="flex items-center space-x-2 text-gray-600 hover:text-blue-600 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Volver</span>
              </button>
              <div className="h-6 w-px bg-gray-300" />
              <div className="flex items-center space-x-3">
                <Database className="w-6 h-6 text-blue-600" />
                <h1 className="text-xl font-bold">Microcuencas - Variables Completas</h1>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                className="hidden"
              />
              <button 
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                <Upload className="w-4 h-4" />
                <span>{uploading ? 'Importando...' : 'Subir CSV Local'}</span>
              </button>
              <button 
                onClick={handleImportCSV}
                disabled={uploading}
                className="flex items-center space-x-2 bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 disabled:opacity-50"
              >
                <Database className="w-4 h-4" />
                <span>Importar Remoto</span>
              </button>
              <button 
                onClick={() => handleOpenModal()}
                className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
              >
                <Plus className="w-4 h-4" />
                <span>Nuevo</span>
              </button>
              {microcuencas.length > 0 && (
                <button 
                  onClick={handleClearAll}
                  className="flex items-center space-x-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Limpiar BD</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </motion.header>

      <div className="container mx-auto px-6 py-8">
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="flex items-center space-x-4">
            <Search className="w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por código, municipio o microcuenca..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-600">
              {filteredData.length} de {microcuencas.length} registros
            </span>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Código</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Municipio</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Microcuenca</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sup. Agrícola</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sup. Total</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Precipitación</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Temperatura</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredData.map((item) => (
                    <tr key={item._id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm">{item.codigo}</td>
                      <td className="px-4 py-3 text-sm">{item.municipio}</td>
                      <td className="px-4 py-3 text-sm">{item.microcuenca}</td>
                      <td className="px-4 py-3 text-sm">{item.superficie_agricola?.toFixed(1)}</td>
                      <td className="px-4 py-3 text-sm">{item.sup_micro?.toFixed(1)}</td>
                      <td className="px-4 py-3 text-sm">{item.precipitacion?.toFixed(1)}</td>
                      <td className="px-4 py-3 text-sm">{item.temperatura?.toFixed(1)}</td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleOpenModal(item)}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(item._id)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div 
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold">
                {editingItem ? 'Editar Registro' : 'Nuevo Registro'}
              </h2>
              <button onClick={handleCloseModal} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Código *</label>
                  <input
                    type="text"
                    required
                    value={formData.codigo || ''}
                    onChange={(e) => setFormData({...formData, codigo: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Municipio *</label>
                  <input
                    type="text"
                    required
                    value={formData.municipio || ''}
                    onChange={(e) => setFormData({...formData, municipio: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1">Microcuenca *</label>
                  <input
                    type="text"
                    required
                    value={formData.microcuenca || ''}
                    onChange={(e) => setFormData({...formData, microcuenca: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Superficie Agrícola</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.superficie_agricola || 0}
                    onChange={(e) => setFormData({...formData, superficie_agricola: parseFloat(e.target.value)})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Superficie Total</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.sup_micro || 0}
                    onChange={(e) => setFormData({...formData, sup_micro: parseFloat(e.target.value)})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Precipitación</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.precipitacion || 0}
                    onChange={(e) => setFormData({...formData, precipitacion: parseFloat(e.target.value)})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Temperatura</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.temperatura || 0}
                    onChange={(e) => setFormData({...formData, temperatura: parseFloat(e.target.value)})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {editingItem ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  )
}

export default MicrocuencasVariables
